import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  DownloadOutlined as DownloadIcon,
  PictureAsPdf as PdfIcon,
  SwapHoriz as ReplaceIcon,
  TableChartOutlined as SpreadsheetIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import { useRef, useState } from 'react';
import { store } from '../../../../app/store';
import {
  useSendInvoiceDeliveryMutation,
  useReplaceInvoiceDeliveryAttachmentMutation,
  useLazyGetInvoiceDeliveryStatusQuery,
  waitForInvoiceDeliveryResult,
  INVOICE_DELIVERY_API,
  type InvoiceDeliveryAttachment,
  type InvoiceDeliveryPreview,
} from '../../api/invoiceDeliveryApi';
import { invalidateGridDataCache } from '../../api/invalidateGridDataCache';
import api from '../../../../services/api';
import {
  downloadBlob,
  openBlobInNewTab,
  resolveBlobErrorMessage,
} from '../../../../utils/blobFileActions';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import { FinanceEmailComposeFields } from './FinanceEmailComposeFields';
import { formatCcList } from './financeEmailComposeUtils';

type SendInvoiceToClientDialogProps = {
  open: boolean;
  invoiceId: number;
  templateId?: string;
  preview: InvoiceDeliveryPreview;
  onClose: () => void;
  onSuccess: (message: string) => void;
};

type ReplacedAttachmentInfo = {
  uploadedFileName: string;
};

export const SendInvoiceToClientDialog = ({
  open,
  invoiceId,
  templateId,
  preview,
  onClose,
  onSuccess,
}: SendInvoiceToClientDialogProps) => {
  const [to, setTo] = useState(preview.to);
  const [cc, setCc] = useState(formatCcList(preview.cc));
  const bcc = formatCcList(preview.bcc);
  const [subject, setSubject] = useState(preview.subject);
  const [message, setMessage] = useState(preview.message);
  const [attachments, setAttachments] = useState<InvoiceDeliveryAttachment[]>(preview.attachments);
  const [replacedAttachments, setReplacedAttachments] = useState<
    Partial<Record<InvoiceDeliveryAttachment['type'], ReplacedAttachmentInfo>>
  >({});
  const [localError, setLocalError] = useState<string | null>(null);
  const [attachmentAction, setAttachmentAction] = useState<string | null>(null);
  const [isWaitingForResult, setIsWaitingForResult] = useState(false);
  const fileInputRefs = useRef<
    Partial<Record<InvoiceDeliveryAttachment['type'], HTMLInputElement | null>>
  >({});

  const [sendInvoice, sendState] = useSendInvoiceDeliveryMutation();
  const [replaceAttachment, replaceState] = useReplaceInvoiceDeliveryAttachmentMutation();
  const [fetchStatus] = useLazyGetInvoiceDeliveryStatusQuery();
  const isSending = sendState.isLoading || isWaitingForResult;
  const isReplacing = replaceState.isLoading;
  const fieldsDisabled = isSending || isReplacing;

  const handleClose = () => {
    if (fieldsDisabled) {
      return;
    }

    onClose();
  };

  const handleSend = async () => {
    setLocalError(null);

    try {
      const queued = await sendInvoice({
        invoiceId,
        to: to.trim(),
        cc: cc.trim(),
        subject: subject.trim(),
        message,
        templateId,
      }).unwrap();

      setIsWaitingForResult(true);

      const result = await waitForInvoiceDeliveryResult(queued.historyId, async (historyId) => {
        const status = await fetchStatus(historyId).unwrap();
        return status;
      });

      invalidateGridDataCache(store.dispatch);

      if ((result.status || '').toLowerCase() === 'failed') {
        setLocalError(result.message || 'Email was not sent. Please try again or contact support');
        return;
      }

      onSuccess(result.message || 'Invoice was successfully sent to the client');
      onClose();
    } catch (error: unknown) {
      setLocalError(
        extractErrorMessage(error, 'Email was not sent. Please try again or contact support')
      );
    } finally {
      setIsWaitingForResult(false);
    }
  };

  const handleAttachmentAction = async (attachment: InvoiceDeliveryAttachment) => {
    setLocalError(null);
    setAttachmentAction(attachment.type);

    try {
      const query =
        attachment.type === 'invoice'
          ? `${INVOICE_DELIVERY_API}/ViewInvoicePdf?invoiceId=${invoiceId}${
              templateId ? `&templateId=${encodeURIComponent(templateId)}` : ''
            }`
          : `${INVOICE_DELIVERY_API}/DownloadTimesheet?invoiceId=${invoiceId}`;

      const response = await api.get(query, { responseType: 'blob' });
      const blob = response.data as Blob;

      if (attachment.type === 'invoice') {
        openBlobInNewTab(blob);
      } else {
        downloadBlob(blob, attachment.name);
      }
    } catch (error: unknown) {
      setLocalError(await resolveBlobErrorMessage(error, 'Failed to open attachment.'));
    } finally {
      setAttachmentAction(null);
    }
  };

  const handleReplaceClick = (attachmentType: InvoiceDeliveryAttachment['type']) => {
    fileInputRefs.current[attachmentType]?.click();
  };

  const handleReplaceFileSelected = async (
    attachmentType: InvoiceDeliveryAttachment['type'],
    fileList: FileList | null
  ) => {
    const file = fileList?.[0];
    const input = fileInputRefs.current[attachmentType];
    if (input) {
      input.value = '';
    }

    if (!file) {
      return;
    }

    setLocalError(null);
    setAttachmentAction(`replace-${attachmentType}`);

    try {
      const updated = await replaceAttachment({
        invoiceId,
        attachmentType,
        file,
      }).unwrap();

      setAttachments((current) =>
        current.map((item) => (item.type === attachmentType ? updated : item))
      );
      setReplacedAttachments((current) => ({
        ...current,
        [attachmentType]: { uploadedFileName: file.name },
      }));
    } catch (error: unknown) {
      setLocalError(extractErrorMessage(error, 'Failed to replace attachment.'));
    } finally {
      setAttachmentAction(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Send invoice to client</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {localError && <Alert severity="error">{localError}</Alert>}

          <FinanceEmailComposeFields
            to={to}
            cc={cc}
            bcc={bcc}
            subject={subject}
            message={message}
            onToChange={setTo}
            onCcChange={setCc}
            onSubjectChange={setSubject}
            onMessageChange={setMessage}
            disabled={fieldsDisabled}
          />

          <Box>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                mb: 0.5,
                color: fieldsDisabled ? 'text.disabled' : 'text.primary',
              }}
            >
              Attachments
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Replace keeps the original file name; only the file content is updated.
            </Typography>
            <Stack
              spacing={1}
              sx={{
                mt: '10px',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                bgcolor: 'background.paper',
              }}
            >
              {attachments.map((attachment) => {
                const isInvoice = attachment.type === 'invoice';
                const isDisabled = Boolean(attachmentAction) || fieldsDisabled;
                const replacedInfo = replacedAttachments[attachment.type];

                return (
                  <Stack
                    key={`${attachment.type}-${attachment.name}`}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      px: 1,
                      py: 0.75,
                      borderRadius: 1,
                      bgcolor: replacedInfo ? 'success.50' : 'action.hover',
                      border: '1px solid',
                      borderColor: replacedInfo ? 'success.light' : 'transparent',
                    }}
                  >
                    {isInvoice ? (
                      <PdfIcon fontSize="small" color="error" sx={{ flexShrink: 0 }} />
                    ) : (
                      <SpreadsheetIcon fontSize="small" color="success" sx={{ flexShrink: 0 }} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography
                          variant="body2"
                          sx={{
                            minWidth: 0,
                            wordBreak: 'break-word',
                            color: fieldsDisabled ? 'text.disabled' : 'text.primary',
                          }}
                        >
                          {attachment.name}
                        </Typography>
                        {replacedInfo && (
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label="Replaced"
                            sx={{ height: 22 }}
                          />
                        )}
                      </Stack>
                      {replacedInfo && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.25, wordBreak: 'break-word' }}
                        >
                          Uploaded: {replacedInfo.uploadedFileName}
                        </Typography>
                      )}
                    </Box>
                    <input
                      ref={(element) => {
                        fileInputRefs.current[attachment.type] = element;
                      }}
                      type="file"
                      accept={
                        isInvoice
                          ? '.pdf,application/pdf'
                          : '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                      }
                      hidden
                      onChange={(event) =>
                        void handleReplaceFileSelected(attachment.type, event.target.files)
                      }
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={isDisabled}
                      onClick={() => void handleAttachmentAction(attachment)}
                      startIcon={
                        attachmentAction === attachment.type ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : isInvoice ? (
                          <ViewIcon fontSize="small" />
                        ) : (
                          <DownloadIcon fontSize="small" />
                        )
                      }
                      sx={{ flexShrink: 0, textTransform: 'none' }}
                    >
                      {attachmentAction === attachment.type
                        ? 'Opening...'
                        : isInvoice
                          ? 'View'
                          : 'Download'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={isDisabled}
                      onClick={() => handleReplaceClick(attachment.type)}
                      startIcon={
                        attachmentAction === `replace-${attachment.type}` ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <ReplaceIcon fontSize="small" />
                        )
                      }
                      sx={{ flexShrink: 0, textTransform: 'none' }}
                    >
                      {attachmentAction === `replace-${attachment.type}`
                        ? 'Replacing...'
                        : 'Replace'}
                    </Button>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={fieldsDisabled}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSend()}
          disabled={fieldsDisabled || !to.trim() || !subject.trim()}
        >
          {isSending ? 'Sending...' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendInvoiceToClientDialog;
